import React from 'react';
import { FieldValue, Form } from '../../../../types/form-types';
import { FieldPosition, Template, TemplateField } from '../../../../types/template-types';
import { ValueChangedCallbackFunc, ValueChangedForFieldFunc, ValueChangedFunc, ValueChangedPayload } from '../../../formeditor-types';
import { checkIfCalculationValueIsTrue, getEditableValue } from '../../../helpers';
import { MissingRequiredField } from '../../../requiredFields';
import { getField, getFormulaValue } from '../../../selectors';
import { DisplayType, FieldTypes } from './../../../../helpers/constants';
import { getRgbaColor } from './../../../../helpers/helpers';
import AudioField from './AudioField';
import AutoNumberField from './AutoNumberField';
import BarcodeField from './BarcodeField';
import CheckBoxField from './CheckBoxField';
import CheckBoxGroupField from './CheckBoxGroupField';
import DatabaseField from './DatabaseField';
import DateField from './DateField';
import DateTimeField from './DateTimeField';
import DropDownField from './DropDownField';
import ESignatureField from './ESignatureField';
import FileAttachmentField from './FileAttachmentField';
import ImageField from './ImageField';
import InputField from './InputField';
import LocationMapField from './LocationMapField';
import LocationTextField from './LocationTextField';
import SignatureField from './SignatureField';
import SketchField from './SketchField';
import TimeField from './TimeField';

const getControlForType = (
  key,
  fieldId,
  fieldType,
  name,
  x,
  y,
  w,
  h,
  foregroundColor,
  backgroundColor,
  focusColor,
  fontName,
  fontSize,
  align,
  lineHeight,
  maxSize,
  padding,
  displayType,
  properties,
  editable,
  value,
  valueUpdated: ValueChangedForFieldFunc,
  templateId,
  templateName,
  formId,
  formName,
  tabIndex,
  tableName,
  characterLimit,
  openOcrImagePickerModal
) => {
  let svg = true;
  let control = null;

  switch (fieldType) {
    case FieldTypes.AutoNumber:
      control = (
        <AutoNumberField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontName={fontName}
          fontSize={fontSize}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldId={fieldId}
          id={key}
        />
      );
      break;

    case FieldTypes.CheckBox:
      control = (
        <CheckBoxField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          displayType={displayType}
          maxSize={maxSize}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldId={fieldId}
          id={key}
        />
      );
      break;

    case FieldTypes.CheckBoxGroup:
      control = (
        <CheckBoxGroupField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          displayType={displayType}
          maxSize={maxSize}
          padding={padding}
          checkBoxValue={properties.checkBoxValue}
          multiSelect={properties.multiSelect}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldId={fieldId}
          id={key}
        />
      );
      break;

    case FieldTypes.Database:
      control = (
        <DatabaseField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontName={fontName}
          fontSize={fontSize}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          dataSource={properties.dataSource}
          dataSourceOrigin={properties.dataSourceOrigin}
          filter={properties.filter}
          displayColumn={properties.displayColumn}
          allowOther={properties.allowOther}
          wrapText={properties.wrapText}
          fieldId={fieldId}
          tableName={tableName}
          id={key}
        />
      );
      break;

    case FieldTypes.DropDown:
      control = (
        <DropDownField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontName={fontName}
          fontSize={fontSize}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          dropdownItems={properties.dropdownItems}
          itemCollectionTemplateId={properties.itemCollectionTemplateId}
          allowOther={properties.allowOther}
          wrapText={properties.wrapText}
          fieldId={fieldId}
          id={key}
        />
      );
      break;

    case FieldTypes.GPS:
      if (displayType === DisplayType.GPSMap) {
        control = (
          <LocationMapField
            key={key}
            x={x}
            y={y}
            w={w}
            h={h}
            align={align}
            backgroundColor={backgroundColor}
            padding={padding}
            editable={editable}
            name={name}
            value={value}
            valueUpdated={valueUpdated}
            backgroundCropDimensions={properties.backgroundCropDimensions}
            formId={formId}
            dateStamp={properties.dateStamp}
            color={properties.field.color}
            fontSize={properties.field.fontSize}
            fontName={properties.field.fontName}
            fieldId={fieldId}
            id={key}
            rowNumber={properties.rowNumber}
          />
        );
      } else {
        control = (
          <LocationTextField
            key={key}
            x={x}
            y={y}
            w={w}
            h={h}
            align={align}
            foregroundColor={foregroundColor}
            backgroundColor={backgroundColor}
            fontName={fontName}
            fontSize={fontSize}
            padding={padding}
            editable={editable}
            name={name}
            value={value}
            valueUpdated={valueUpdated}
            requireMapImage={properties.requireMapImage}
            backgroundCropDimensions={properties.backgroundCropDimensions}
            formId={formId}
            fieldId={fieldId}
            id={key}
            dateStamp={properties.dateStamp}
            fieldColor={properties.field.color}
            fieldFontSize={properties.field.fontSize}
            fieldFontName={properties.field.fontName}
            rowNumber={properties.rowNumber}
          />
        );
      }
      break;

    case FieldTypes.Date:
      control = (
        <DateField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontName={fontName}
          fontSize={fontSize}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldType={fieldType}
          useCurrent={properties.useCurrent}
          fieldId={fieldId}
          id={key}
          dateTimeFormat={properties.dateTimeFormat}
        />
      );
      break;
    case FieldTypes.DateTime:
      control = (
        <DateTimeField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontName={fontName}
          fontSize={fontSize}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldType={fieldType}
          useCurrent={properties.useCurrent}
          fieldId={fieldId}
          id={key}
          dateTimeFormat={properties.dateTimeFormat}
        />
      );
      break;
    case FieldTypes.Time:
      control = (
        <TimeField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontName={fontName}
          fontSize={fontSize}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldType={fieldType}
          useCurrent={properties.useCurrent}
          fieldId={fieldId}
          id={key}
          dateTimeFormat={properties.dateTimeFormat}
        />
      );
      break;

    case FieldTypes.Sketch:
      control = (
        <SketchField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          backgroundColor={backgroundColor}
          padding={padding}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          formId={formId}
          fieldId={fieldId}
          id={key}
          backgroundCropDimensions={properties.backgroundCropDimensions}
          imageQuality={properties.imageQuality}
          rowNumber={properties.rowNumber}
          dateStamp={properties.dateStamp}
          color={properties.field.color}
          fontSize={properties.field.fontSize}
          fontName={properties.field.fontName}
        />
      );
      break;

    case FieldTypes.Signature:
      control = (
        <SignatureField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          padding={padding}
          editable={editable}
          name={name}
          value={value}
          valueUpdated={valueUpdated}
          formId={formId}
          backgroundCropDimensions={properties.backgroundCropDimensions}
          fieldId={fieldId}
          signingMethod={properties.signingMethod}
          color={properties.field.color}
          id={key}
          rowNumber={properties.rowNumber}
        />
      );
      break;

    case FieldTypes.eSignature:
    case FieldTypes.eSignatureInitials:
      control = (
        <ESignatureField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          backgroundColor={backgroundColor}
          padding={padding}
          editable={editable}
          name={name}
          value={value}
          valueUpdated={valueUpdated}
          templateId={templateId}
          templateName={templateName}
          formId={formId}
          fieldId={fieldId}
          signingMethod={properties.signingMethod}
          fieldType={fieldType}
          id={key}
        />
      );
      break;

    case FieldTypes.Barcode:
      control = (
        <BarcodeField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontName={fontName}
          fontSize={fontSize}
          padding={padding}
          editable={editable}
          name={name}
          value={value}
          valueUpdated={valueUpdated}
          id={key}
          fieldId={fieldId}
        />
      );
      break;

    case FieldTypes.Image:
      control = (
        <ImageField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          maxSize={maxSize}
          backgroundColor={backgroundColor}
          padding={padding}
          editable={editable}
          name={name}
          value={value}
          valueUpdated={valueUpdated}
          imageQuality={properties.imageQuality}
          retainImageAsAttachment={properties.retainImageAsAttachment}
          dateStamp={properties.dateStamp}
          color={properties.field.color}
          fontSize={properties.field.fontSize}
          fontName={properties.field.fontName}
          formId={formId}
          fieldId={fieldId}
          id={key}
          rowNumber={properties.rowNumber}
          openOcrImagePickerModal={openOcrImagePickerModal}
        />
      );
      break;

    case FieldTypes.FileAttachment:
      control = (
        <FileAttachmentField
          key={key}
          fieldType={fieldType}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontSize={fontSize}
          fontName={fontName}
          focusColor={focusColor}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldId={fieldId}
          id={key}
          view="form"
          templateId={templateId}
          templateName={templateName}
          formId={formId}
          formName={formName}
          tabIndex={tabIndex}
          maxFileCount={properties.maxFileCount}
          maxFileSize={properties.maxFileSize}
          allowedFileTypes={properties.allowedFileTypes}
          name={name}
        />
      );
      break;

    case FieldTypes.Audio:
      control = (
        <AudioField
          key={key}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          fontSize={fontSize}
          fontName={fontName}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldId={fieldId}
          id={key}
          formId={formId}
          tabIndex={tabIndex}
          maxLength={properties.maxLength}
          name={name}
        />
      );
      break;

    case FieldTypes.TextBox:
    case FieldTypes.TextArea:
    case FieldTypes.Number:
    default:
      svg = false;
      control = (
        <InputField
          key={key}
          fieldType={fieldType}
          x={x}
          y={y}
          w={w}
          h={h}
          align={align}
          foregroundColor={foregroundColor}
          backgroundColor={backgroundColor}
          focusColor={focusColor}
          padding={padding}
          fontName={fontName}
          fontSize={fontSize}
          lineHeight={lineHeight}
          editable={editable}
          value={value}
          valueUpdated={valueUpdated}
          fieldId={fieldId}
          id={key}
          tabIndex={tabIndex}
          characterLimit={characterLimit}
        />
      );
      break;
  }

  return {
    svg,
    control
  };
};

export const getPropertyFormulaValue = (formulaValues: Record<string, TypeMePlease>, id, propertyName, value?) => {
  const formulaValue = getFormulaValue(formulaValues, id, propertyName);
  if (formulaValue.hasFormula) {
    return formulaValue;
  }
  return { hasFormula: false, value };
};

//TODO: use this later when we write return index handling on our table fields
export const getTableFieldReturnIndex = (index, rowIndex, columnIndex, numRows) => {
  return numRows * columnIndex + rowIndex + index;
};

export const getTableFieldTabIndex = (index, rowIndex, sortOrder, numCols) => {
  return numCols * rowIndex + sortOrder + index;
};

const getControlForTableCell = (
  template: TypeMePlease,
  form: TypeMePlease,
  x: number,
  y: number,
  w: number,
  h: number,
  column: TypeMePlease,
  columnPosition: TypeMePlease,
  rowNumber: number,
  rowPosition: TypeMePlease,
  fieldPosition: FieldPosition,
  field: TemplateField,
  fieldValue: FieldValue,
  updateFieldValue: ValueChangedForFieldFunc,
  formulaValues: Record<string, TypeMePlease>,
  userId: string,
  isAdmin: boolean,
  tabIndex: number,
  tableName: string,
  openOcrImagePickerModal?: AnyFunc
) => {
  const value = (fieldValue ? fieldValue.value : '') || '';

  const foregroundColor = getRgbaColor(getPropertyFormulaValue(formulaValues, fieldPosition.id, 'Color', fieldPosition.color).value, '#000000', 1);

  const backgroundColor = getRgbaColor(
    getPropertyFormulaValue(formulaValues, fieldPosition.id, 'BackgroundColor', fieldPosition.backgroundColor).value,
    '#FFFF99',
    0.7
  );
  const focusColor = getRgbaColor(fieldPosition.focusColor, '#00FFFF', 0.25);

  let padding = columnPosition.padding;
  if (padding === undefined || padding === null) {
    padding = fieldPosition.padding;
  }

  let fontSize = fieldPosition.fontSize;
  if (fontSize === undefined) {
    fontSize = 16;
  }

  let checkBoxValue = null;
  if (columnPosition.itemId && column.items) {
    const item = column.items.find((p) => p.id === columnPosition.itemId);
    if (item) {
      checkBoxValue = item.value;
    }
  }

  let filter = null;
  const filters = column.filters;
  if (filters && filters.length > rowNumber) {
    filter = filters[rowNumber];
  }

  const key = `${fieldPosition.id}_${column.columnNumber}_${rowNumber}`; //_${value}_${updatecount}

  const tabIndexForField = getTableFieldTabIndex(tabIndex, rowPosition.rowNumber, column.sortOrder, fieldPosition.columns.length);
  //const returnIndex = getTableFieldReturnIndex(index, rowPosition.rowNumber, column.columnNumber, fieldPosition.rows.size);
  const backgroundCropDimensions = { x, y, w, h, pageNumber: field.backgroundCropDimensions?.pageNumber };

  return getControlForType(
    key,
    column.id,
    column.type,
    column.name,
    x,
    y,
    w,
    h,
    foregroundColor,
    backgroundColor,
    focusColor,
    fieldPosition.fontName,
    fontSize,
    columnPosition.align,
    calculateLineHeight(columnPosition.lineHeight, fontSize),
    columnPosition.maxSize,
    padding,
    columnPosition.displayType,
    {
      multiSelect: column.multiSelect,
      checkBoxValue,
      dataSource: column.dataSource,
      displayColumn: column.displayColumn,
      requireMapImage: column.requireMapImage,
      backgroundCropDimensions,
      allowOther: column.allowOther,
      itemCollectionTemplateId: column.itemCollectionTemplateId,
      dropdownItems: column.items || null,
      useCurrent: column.useCurrent,
      filter,
      imageQuality: column.imageQuality,
      retainImageAsAttachment: column.retainImageAsAttachment,
      dateStamp: column.dateStamp,
      rowNumber: rowPosition.rowNumber,
      signingMethod: column.signingMethod,
      field: {
        color: getRgbaColor(column.color, '#000000', 1),
        fontName: column.fontName,
        fontSize: column.fontSize || 16
      },
      wrapText: columnPosition.wrapText,
      dateTimeFormat: column.dateTimeFormat
    },
    getEditableValue(form, column, value, userId, isAdmin),
    value,
    updateFieldValue,
    template.id,
    template.name,
    form.id,
    form.name,
    tabIndexForField,
    tableName,
    column.characterLimit,
    openOcrImagePickerModal
  );
};

export const getControlsForTablePosition = (
  template,
  form,
  fieldPosition,
  field,
  fieldValue,
  updateFieldValue: ValueChangedFunc,
  formulaValues: Record<string, TypeMePlease>,
  userId,
  isAdmin,
  modifiedTabIndex,
  openOcrImagePickerModal
) => {
  const controls = [];
  const columns = field.columns;
  const columnPositions = fieldPosition.columns;
  const rowPositions = fieldPosition.rows;
  const numberOfRows = fieldPosition.numberOfRows;
  const startingRow = fieldPosition.startingRow;

  if (columns) {
    const sortedColumns = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const column of sortedColumns) {
      const columnPosition = columnPositions.find((x) => x.columnId === column.id);

      if (!columnPosition.hidden && !columnPosition.transparent) {
        let y = 0;
        for (let positionRowNumber = 0; positionRowNumber < numberOfRows; positionRowNumber++) {
          const rowNumber = positionRowNumber + startingRow - 1;
          const rowPosition = rowPositions.find((x) => x.rowNumber === positionRowNumber);
          const rowHeight = rowPosition.h;

          const cellX = fieldPosition.x + columnPosition.x;
          const cellY = fieldPosition.y + y;
          const cellW = columnPosition.w;
          const cellH = rowHeight - 1;

          const rowValues = fieldValue ? fieldValue.rows : null;
          const rowValue = rowValues ? rowValues.find((x) => x.rowNumber === rowNumber) : null;
          const columnValues = rowValue ? rowValue.columns : null;
          const cellValue = columnValues ? columnValues.find((x) => x.columnNumber === column.columnNumber) : null;

          controls.push(
            getControlForTableCell(
              template,
              form,
              cellX,
              cellY,
              cellW,
              cellH,
              column,
              columnPosition,
              rowNumber,
              rowPosition,
              fieldPosition,
              field,
              cellValue,
              (v: ValueChangedPayload, callback: ValueChangedCallbackFunc) => {
                updateFieldValue(v, field, rowNumber, column, callback);
              },
              formulaValues,
              userId,
              isAdmin,
              modifiedTabIndex,
              field.name,
              openOcrImagePickerModal
            )
          );
          y += rowHeight;
        }
      }
    }
  }

  return controls;
};

export const getModifiedTabIndices = (fieldPositions: FieldPosition[], template: TypeMePlease, pageNum: number): Record<string, number> => {
  const modifiedFieldPositions = {};
  let tableIndexOffset = 1000 * pageNum;

  const fieldPositionsByIndex = [...fieldPositions].sort((a, b) => a.tabIndex - b.tabIndex);

  for (const fieldPosition of fieldPositionsByIndex) {
    const fieldType = getField(template, fieldPosition.fieldId).type;
    modifiedFieldPositions[fieldPosition.id] = (fieldPosition?.tabIndex ?? 0) + tableIndexOffset;

    if (fieldType === FieldTypes.Table) {
      //calculate how much index space is taken up by this table
      const cellsInTable = fieldPosition.rows.length * fieldPosition.columns.length;
      tableIndexOffset += cellsInTable;
    }
  }

  return modifiedFieldPositions;
};

export const getControlsForFieldPosition = (
  template: Template,
  form: Form,
  fieldPosition: FieldPosition,
  field: TemplateField,
  fieldValue,
  updateFieldValue: ValueChangedFunc,
  formulaValues: Record<string, TypeMePlease>,
  userId,
  isAdmin,
  modifiedTabIndex,
  missingRequiredFields: MissingRequiredField[],
  openOcrImagePickerModal?: AnyFunc
) => {
  let controls = [];
  const fieldType = field.type;

  const value = (fieldValue ? fieldValue.value : '') || '';

  const visibleFormula = getPropertyFormulaValue(formulaValues, fieldPosition.id, 'Visible', null);
  if ((visibleFormula.hasFormula && !checkIfCalculationValueIsTrue(visibleFormula.value)) || (!visibleFormula.hasFormula && fieldPosition.visible === false)) {
    return [];
  }

  if (fieldType === FieldTypes.Table) {
    controls = controls.concat(
      getControlsForTablePosition(
        template,
        form,
        fieldPosition,
        field,
        fieldValue,
        updateFieldValue,
        formulaValues,
        userId,
        isAdmin,
        modifiedTabIndex,
        openOcrImagePickerModal
      )
    );
  } else {
    const foregroundColor = getRgbaColor(getPropertyFormulaValue(formulaValues, fieldPosition.id, 'Color', fieldPosition.color).value, '#000000', 1);
    const focusColor = getRgbaColor(fieldPosition.focusColor, '#00FFFF', 0.25);
    let backgroundColor;
    if (template.requiredBackgroundColor && missingRequiredFields && missingRequiredFields.some((x) => x.id === field.id)) {
      backgroundColor = getRgbaColor(template.requiredBackgroundColor, '#FFFF99', 0.7);
    } else {
      backgroundColor = getRgbaColor(
        getPropertyFormulaValue(formulaValues, fieldPosition.id, 'BackgroundColor', fieldPosition.backgroundColor).value,
        '#FFFF99',
        0.7
      );
    }

    let fontSize = fieldPosition.fontSize;
    if (fontSize === undefined) {
      fontSize = 16;
    }

    let checkBoxValue = null;
    if (fieldPosition.itemId && field.items) {
      const item = field.items.find((p) => p.id === fieldPosition.itemId);
      if (item) {
        checkBoxValue = item.value;
      }
    }

    const key = `${fieldPosition.id}`; //_${value}_${updatecount}

    controls.push(
      getControlForType(
        key,
        field.id,
        fieldType,
        field.name,
        fieldPosition.x,
        fieldPosition.y,
        fieldPosition.w,
        fieldPosition.h,
        foregroundColor,
        backgroundColor,
        focusColor,
        fieldPosition.fontName,
        fontSize,
        fieldPosition.align,
        calculateLineHeight(fieldPosition.lineHeight, fontSize),
        fieldPosition.maxSize,
        fieldPosition.padding,
        fieldPosition.displayType,
        {
          multiSelect: field.multiSelect,
          checkBoxValue,
          dataSource: field.dataSource,
          dataSourceOrigin: field.dataSourceOrigin,
          displayColumn: field.displayColumn,
          requireMapImage: field.requireMapImage,
          backgroundCropDimensions: field.backgroundCropDimensions || null,
          allowOther: field.allowOther,
          itemCollectionTemplateId: field.itemCollectionTemplateId,
          dropdownItems: field.items || null,
          useCurrent: field.useCurrent,
          filter: field.filter,
          imageQuality: field.imageQuality,
          retainImageAsAttachment: field.retainImageAsAttachment,
          dateStamp: field.dateStamp,
          wrapText: fieldPosition.wrapText,
          signingMethod: field.signingMethod,
          field: {
            color: getRgbaColor(field.color, '#000000', 1),
            fontName: field.fontName,
            fontSize: field.fontSize || 16
          },
          dateTimeFormat: field.dateTimeFormat,
          maxFileCount: field.maxFileCount,
          maxFileSize: field.maxFileSize,
          allowedFileTypes: field.allowedFileTypes,
          maxLength: field.maxLength
        },
        getEditableValue(form, field, value, userId, isAdmin),
        value,
        (v: ValueChangedPayload, callback: ValueChangedCallbackFunc) => {
          updateFieldValue(v, field, undefined, undefined, callback);
        },
        template.id,
        template.name,
        form.id,
        form.name,
        modifiedTabIndex,
        null,
        field.characterLimit,
        openOcrImagePickerModal
      )
    );
  }
  return controls;
};

export function calculateLineHeight(lineHeight: number, fontSize: number): number {
  if (!lineHeight) {
    return Math.max(Math.ceil(fontSize * 1.1), fontSize + 2);
  }
  return lineHeight;
}
